import type { Data } from "@puckeditor/core";
import {
  sanitizeComponentId,
  generateScopedId,
} from "@/components/pages/style-state-system";
import type { SavedSectionBlock, SavedSectionPuckData } from "@/hooks/use-saved-sections";

export type BuilderBlock = SavedSectionBlock;
export type BuilderZones = Record<string, BuilderBlock[]>;
export type BlockCollection = "content" | "zone";

interface BlockLocation {
  collection: BlockCollection;
  zoneKey?: string;
  index: number;
}

export type SiteShellType = "header" | "footer";
export type EditorMode = "simple" | "full";
export type EditorEntry = "generated" | "template" | "manual";

export interface SiteShellRecord {
  id: string;
  name: string;
  slug: string;
  headerContent: unknown;
  footerContent: unknown;
  globalTheme?: unknown;
}

export const INITIAL_DATA: Data = {
  content: [],
  root: {},
};

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function cloneValue<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

export function ensureItemId<T>(item: T, fallbackPrefix: string, index: number): T {
  if (!isRecord(item)) return item;

  const props = isRecord(item.props) ? { ...item.props } : {};
  const currentId = typeof props.id === "string" ? sanitizeComponentId(props.id) : "";
  props.id = currentId || `${fallbackPrefix}-${index + 1}`;

  return {
    ...item,
    props,
  } as T;
}

function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function splitLines(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => readString(item))
      .filter(Boolean);
  }

  if (typeof value !== "string") {
    return [];
  }

  return value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizePricingPropsForEditor<T>(item: T): T {
  if (!isRecord(item) || item.type !== "PricingTable" || !isRecord(item.props)) {
    return item;
  }

  const props = item.props;

  if (Array.isArray(props.tiers)) {
    return item;
  }

  const tiers = [1, 2, 3]
    .map((index) => {
      const name = readString(props[`tier${index}Name`]);
      const price = readString(props[`tier${index}Price`]);
      const features = splitLines(props[`tier${index}Features`]);
      if (!name && !price && features.length === 0) return null;
      return {
        name,
        price,
        features,
        ctaText: `Choose ${name || `Tier ${index}`}`,
        isPopular: index === 2,
      };
    })
    .filter((tier): tier is NonNullable<typeof tier> => tier !== null);

  if (tiers.length === 0) {
    return item;
  }

  return {
    ...item,
    props: {
      ...props,
      tiers,
    },
  } as T;
}

export function normalizePageData(nextData: Data): Data {
  const content = Array.isArray(nextData.content) ? nextData.content : [];
  const zones = isRecord(nextData.zones) ? nextData.zones : undefined;
  const normalizedContent = content.map((item, index) => normalizePricingPropsForEditor(
    ensureItemId(item, "block", index),
  ));
  const normalizedZones = zones
    ? Object.fromEntries(
        Object.entries(zones).map(([zoneKey, zoneItems]) => [
          zoneKey,
          Array.isArray(zoneItems)
            ? zoneItems.map((item, index) => normalizePricingPropsForEditor(
              ensureItemId(item, `${zoneKey}-block`, index),
            ))
            : zoneItems,
        ]),
      ) as Data["zones"]
    : nextData.zones;

  return {
    ...nextData,
    content: normalizedContent,
    zones: normalizedZones,
  };
}

export function buildSavedSectionPayload(nextData: Data): SavedSectionPuckData {
  const normalized = normalizePageData(nextData);
  const content = Array.isArray(normalized.content)
    ? normalized.content.map((item) => cloneValue(item as BuilderBlock))
    : [];
  const root = isRecord(normalized.root) ? cloneValue(normalized.root) : {};
  const zones = isRecord(normalized.zones)
    ? Object.fromEntries(
        Object.entries(normalized.zones).map(([zoneKey, zoneItems]) => [
          zoneKey,
          Array.isArray(zoneItems)
            ? zoneItems.map((item) => cloneValue(item as BuilderBlock))
            : [],
        ]),
      ) as BuilderZones
    : undefined;

  return {
    content,
    root,
    ...(zones ? { zones } : {}),
  };
}

function findBlockById(
  nextData: Data,
  componentId: string,
): BuilderBlock | null {
  const collections = [
    Array.isArray(nextData.content) ? nextData.content : [],
    ...Object.values(isRecord(nextData.zones) ? nextData.zones : {}).filter(Array.isArray),
  ];

  for (const collection of collections) {
    const match = collection.find((item) => isRecord(item.props) && item.props.id === componentId);
    if (match) {
      return match as BuilderBlock;
    }
  }

  return null;
}

function findBlockLocation(
  nextData: Data,
  componentId: string,
): BlockLocation | null {
  const content = Array.isArray(nextData.content) ? nextData.content : [];
  const contentIndex = content.findIndex((item) => isRecord(item.props) && item.props.id === componentId);
  if (contentIndex >= 0) {
    return {
      collection: "content",
      index: contentIndex,
    };
  }

  if (!isRecord(nextData.zones)) {
    return null;
  }

  for (const [zoneKey, zoneItems] of Object.entries(nextData.zones)) {
    if (!Array.isArray(zoneItems)) {
      continue;
    }
    const zoneIndex = zoneItems.findIndex((item) => isRecord(item.props) && item.props.id === componentId);
    if (zoneIndex >= 0) {
      return {
        collection: "zone",
        zoneKey,
        index: zoneIndex,
      };
    }
  }

  return null;
}

function collectDescendantZones(
  zones: BuilderZones | undefined,
  parentId: string,
  collected: BuilderZones,
): void {
  if (!zones) return;

  for (const [zoneKey, zoneItems] of Object.entries(zones)) {
    if (!zoneKey.startsWith(`${parentId}:`) || collected[zoneKey]) {
      continue;
    }

    const clonedItems = zoneItems.map((item) => cloneValue(item));
    collected[zoneKey] = clonedItems;

    for (const child of zoneItems) {
      const childId = isRecord(child.props) && typeof child.props.id === "string"
        ? child.props.id
        : null;
      if (childId) {
        collectDescendantZones(zones, childId, collected);
      }
    }
  }
}

export function buildSavedSectionPayloadForSelection(
  nextData: Data,
  componentId: string | null | undefined,
): SavedSectionPuckData {
  if (!componentId) {
    return buildSavedSectionPayload(nextData);
  }

  const normalized = normalizePageData(nextData);
  const selectedBlock = findBlockById(normalized, componentId);
  if (!selectedBlock) {
    return buildSavedSectionPayload(normalized);
  }

  const selectedId = isRecord(selectedBlock.props) && typeof selectedBlock.props.id === "string"
    ? selectedBlock.props.id
    : componentId;
  const zones = isRecord(normalized.zones) ? normalized.zones as BuilderZones : undefined;
  const subtreeZones: BuilderZones = {};
  collectDescendantZones(zones, selectedId, subtreeZones);

  return {
    content: [cloneValue(selectedBlock)],
    root: {},
    ...(Object.keys(subtreeZones).length > 0 ? { zones: subtreeZones } : {}),
  };
}

export function removeSelectedSubtree(
  nextData: Data,
  componentId: string | null | undefined,
): Data {
  if (!componentId) {
    return normalizePageData(nextData);
  }

  const normalized = normalizePageData(nextData);
  const location = findBlockLocation(normalized, componentId);
  if (!location) {
    return normalized;
  }

  const nextContent = Array.isArray(normalized.content) ? [...normalized.content] : [];
  const nextZones = isRecord(normalized.zones)
    ? Object.fromEntries(
        Object.entries(normalized.zones).map(([zoneKey, zoneItems]) => [
          zoneKey,
          Array.isArray(zoneItems) ? zoneItems.map((item) => cloneValue(item as BuilderBlock)) : [],
        ]),
      ) as BuilderZones
    : {};

  if (location.collection === "content") {
    nextContent.splice(location.index, 1);
  } else if (location.zoneKey) {
    const zoneItems = [...(nextZones[location.zoneKey] ?? [])];
    zoneItems.splice(location.index, 1);
    nextZones[location.zoneKey] = zoneItems;
  }

  const descendantZones: BuilderZones = {};
  collectDescendantZones(nextZones, componentId, descendantZones);
  for (const zoneKey of Object.keys(descendantZones)) {
    delete nextZones[zoneKey];
  }

  return normalizePageData({
    ...normalized,
    content: nextContent as Data["content"],
    ...(Object.keys(nextZones).length > 0 ? { zones: nextZones as Data["zones"] } : { zones: undefined }),
  });
}

export function remapZoneKey(zoneKey: string, idMap: Map<string, string>): string {
  let nextZoneKey = zoneKey;

  for (const [sourceId, targetId] of Array.from(idMap.entries()).sort((left, right) => right[0].length - left[0].length)) {
    nextZoneKey = nextZoneKey.replaceAll(sourceId, targetId);
  }

  return nextZoneKey;
}

export function cloneSavedSectionForInsertion(
  puckData: SavedSectionPuckData,
): Pick<Data, "content" | "zones"> {
  const idMap = new Map<string, string>();
  const planBlockClone = (block: BuilderBlock): BuilderBlock => {
    const props = isRecord(block.props) ? cloneValue(block.props) : {};
    const sourceId = typeof props.id === "string" ? sanitizeComponentId(props.id) : "";
    const nextId = generateScopedId();

    if (sourceId) {
      idMap.set(sourceId, nextId);
    }

    return {
      type: block.type,
      props: {
        ...props,
        id: nextId,
      },
    };
  };

  const content = Array.isArray(puckData.content)
    ? puckData.content.map(planBlockClone)
    : [];
  const plannedZones = isRecord(puckData.zones)
    ? Object.entries(puckData.zones).map(([zoneKey, zoneItems]) => [
      zoneKey,
      Array.isArray(zoneItems) ? zoneItems.map(planBlockClone) : [],
    ] as const)
    : [];
  const zones = isRecord(puckData.zones)
    ? Object.fromEntries(
        plannedZones.map(([zoneKey, zoneItems]) => [
          remapZoneKey(zoneKey, idMap),
          zoneItems,
        ]),
      ) as BuilderZones
    : undefined;

  return {
    content,
    ...(zones ? { zones } : {}),
  };
}

export function insertSavedSectionNearSelection(
  nextData: Data,
  selectionId: string | null | undefined,
  puckData: SavedSectionPuckData,
): Data {
  const normalized = normalizePageData(nextData);
  const insertable = cloneSavedSectionForInsertion(puckData);
  const location = selectionId ? findBlockLocation(normalized, selectionId) : null;

  if (!location) {
    return normalizePageData({
      ...normalized,
      content: [
        ...(Array.isArray(normalized.content) ? normalized.content : []),
        ...(insertable.content ?? []),
      ] as Data["content"],
      zones: mergeZones(normalized.zones, insertable.zones),
    });
  }

  if (location.collection === "content") {
    const nextContent = [...(Array.isArray(normalized.content) ? normalized.content : [])];
    nextContent.splice(location.index + 1, 0, ...(insertable.content ?? []));
    return normalizePageData({
      ...normalized,
      content: nextContent as Data["content"],
      zones: mergeZones(normalized.zones, insertable.zones),
    });
  }

  const nextZones = isRecord(normalized.zones)
    ? Object.fromEntries(
        Object.entries(normalized.zones).map(([zoneKey, zoneItems]) => [
          zoneKey,
          Array.isArray(zoneItems) ? [...zoneItems] : [],
        ]),
      ) as BuilderZones
    : {};
  const zoneKey = location.zoneKey ?? "";
  const zoneItems = [...(nextZones[zoneKey] ?? [])];
  zoneItems.splice(location.index + 1, 0, ...(insertable.content ?? []));
  nextZones[zoneKey] = zoneItems;

  return normalizePageData({
    ...normalized,
    zones: mergeZones(nextZones as Data["zones"], insertable.zones),
  });
}

export function duplicateSelectedSubtree(
  nextData: Data,
  componentId: string | null | undefined,
): Data {
  if (!componentId) {
    return normalizePageData(nextData);
  }

  return insertSavedSectionNearSelection(
    nextData,
    componentId,
    buildSavedSectionPayloadForSelection(nextData, componentId),
  );
}

export function moveSelectedBlock(
  nextData: Data,
  componentId: string | null | undefined,
  direction: -1 | 1,
): Data {
  if (!componentId) {
    return normalizePageData(nextData);
  }

  const normalized = normalizePageData(nextData);
  const location = findBlockLocation(normalized, componentId);
  if (!location) {
    return normalized;
  }

  const items = location.collection === "content"
    ? [...(Array.isArray(normalized.content) ? normalized.content : [])]
    : [...((isRecord(normalized.zones) ? normalized.zones[location.zoneKey ?? ""] : []) ?? [])];
  const nextIndex = location.index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) {
    return normalized;
  }

  const [moved] = items.splice(location.index, 1);
  if (!moved) {
    return normalized;
  }
  items.splice(nextIndex, 0, moved);

  if (location.collection === "content") {
    return normalizePageData({
      ...normalized,
      content: items as Data["content"],
    });
  }

  const nextZones = isRecord(normalized.zones)
    ? Object.fromEntries(
        Object.entries(normalized.zones).map(([zoneKey, zoneItems]) => [
          zoneKey,
          zoneKey === location.zoneKey ? items : Array.isArray(zoneItems) ? [...zoneItems] : [],
        ]),
      ) as Data["zones"]
    : undefined;

  return normalizePageData({
    ...normalized,
    zones: nextZones,
  });
}

export function mergeZones(
  currentZones: Data["zones"],
  insertedZones: Data["zones"],
): Data["zones"] {
  const existing = isRecord(currentZones) ? currentZones : {};
  const next = isRecord(insertedZones) ? insertedZones : {};

  if (Object.keys(next).length === 0) {
    return Object.keys(existing).length > 0 ? existing as Data["zones"] : undefined;
  }

  return {
    ...existing,
    ...next,
  } as Data["zones"];
}

export function heatmapTone(attention: number): string {
  if (attention > 0.7) return "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-200";
  if (attention > 0.4) return "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-200";
  return "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-200";
}

export function heatmapOverlayColor(attention: number): string {
  if (attention > 0.7) return "rgba(239, 68, 68, 0.14)";
  if (attention > 0.4) return "rgba(245, 158, 11, 0.12)";
  return "rgba(14, 165, 233, 0.1)";
}

export function normalizeShellType(value: string | null): SiteShellType | null {
  if (value === "header" || value === "footer") {
    return value;
  }
  return null;
}

export function normalizeEditorMode(value: string | null): EditorMode | null {
  if (value === "simple" || value === "full") {
    return value;
  }
  return null;
}

export function normalizeEditorEntry(value: string | null): EditorEntry {
  if (value === "generated" || value === "template") {
    return value;
  }
  return "manual";
}

export function readShellData(value: unknown): Data {
  if (!isRecord(value)) {
    return INITIAL_DATA;
  }

  const content = Array.isArray(value.content) ? value.content : [];
  const root = isRecord(value.root) ? value.root : {};
  const zones = isRecord(value.zones) ? value.zones as Data["zones"] : undefined;

  return {
    content: content as Data["content"],
    root,
    ...(zones ? { zones } : {}),
  };
}

/** Validate and clamp columns to a safe integer range for CSS grid */
export function safeColumns(value: unknown): number {
  const num = typeof value === "number" ? value : parseInt(String(value), 10);
  if (Number.isNaN(num) || num < 1) return 1;
  if (num > 12) return 12;
  return Math.floor(num);
}

export function sanitizeHref(url: string): string {
  const trimmed = url.trim().toLowerCase();
  if (trimmed.startsWith("javascript:") || trimmed.startsWith("data:") || trimmed.startsWith("vbscript:")) {
    return "#";
  }
  return url;
}

/** Validate image src — only allow http(s), relative paths, and data:image URIs */
export function sanitizeImageSrc(src: string): string {
  const trimmed = src.trim();
  if (trimmed.startsWith("/") || trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:image/")) {
    return trimmed;
  }
  return "";
}
